import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePageTitle } from "@/hooks/use-page-title";
import { adsApi, type CampaignSummary } from "@/lib/ads/ads-api";
import type { TagClass } from "@/lib/status";

/** Formate un montant dans la devise du compte pub (défaut USD si inconnue). */
const fmtMoney = (n?: number | null, currency?: string | null) =>
	n == null
		? "—"
		: new Intl.NumberFormat("fr-FR", {
				style: "currency",
				currency: currency ?? "USD",
				maximumFractionDigits: 2,
			}).format(n);

/** Paramètres du champ budget selon la devise (pas / min / défaut adaptés aux décimales). */
function budgetMeta(currency?: string | null) {
	const cur = currency ?? "USD";
	let digits = 2;
	try {
		digits =
			new Intl.NumberFormat("en", {
				style: "currency",
				currency: cur,
			}).resolvedOptions().maximumFractionDigits ?? 2;
	} catch {
		/* garde 2 décimales par défaut */
	}
	const zeroDecimal = digits === 0;
	return {
		currency: cur,
		step: zeroDecimal ? 500 : 1,
		min: zeroDecimal ? 500 : 1,
		default: zeroDecimal ? 2000 : 10,
	};
}

/** Couleur du tag selon le statut effectif de la campagne Meta. */
function statusTag(
	effective?: string,
	status?: string,
): { label: string; cls: TagClass } {
	const s = (effective ?? status ?? "").toUpperCase();
	if (s === "ACTIVE") return { label: "Active", cls: "success" };
	if (s === "PAUSED") return { label: "En pause", cls: "neutral" };
	if (s.includes("PENDING") || s.includes("REVIEW"))
		return { label: "En revue", cls: "warning" };
	if (s.includes("DISAPPROVED") || s.includes("ERROR"))
		return { label: "Rejetée", cls: "danger" };
	return { label: s || "—", cls: "info" };
}

export function AdminAdsPage() {
	usePageTitle("Publicités");
	const toast = useToast();
	const qc = useQueryClient();

	const [name, setName] = useState("");
	const [dailyBudget, setDailyBudget] = useState<number | null>(null);
	const [audience, setAudience] = useState<"retargeting" | "prospecting">(
		"retargeting",
	);

	const status = useQuery({
		queryKey: ["ads", "status"],
		queryFn: () => adsApi.status(),
	});

	const currency = status.data?.currency;
	const bMeta = budgetMeta(currency);
	// Initialise le budget par défaut dès que la devise du compte est connue.
	useEffect(() => {
		if (dailyBudget == null && status.data) setDailyBudget(bMeta.default);
	}, [status.data, dailyBudget, bMeta.default]);
	const campaigns = useQuery({
		queryKey: ["ads", "campaigns"],
		queryFn: () => adsApi.campaigns(),
		enabled: !!status.data?.enabled,
	});

	const create = useMutation({
		mutationFn: () =>
			adsApi.createCatalog({
				name: name.trim(),
				dailyBudget: dailyBudget ?? bMeta.default,
				audience,
			}),
		onSuccess: (r) => {
			if (r.ok) {
				toast.success(
					"Campagne catalogue créée (en pause). Activez-la pour lancer la diffusion.",
				);
				setName("");
				void qc.invalidateQueries({ queryKey: ["ads", "campaigns"] });
			} else {
				toast.error(r.error ?? "Création impossible.");
			}
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const toggle = useMutation({
		mutationFn: (c: CampaignSummary) =>
			adsApi.setStatus(
				c.id,
				(c.effectiveStatus ?? c.status) === "ACTIVE" ? "PAUSED" : "ACTIVE",
			),
		onSuccess: (r) => {
			if (r.ok) void qc.invalidateQueries({ queryKey: ["ads", "campaigns"] });
			else toast.error(r.error ?? "Action impossible.");
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const enabled = status.data?.enabled;
	const catalogReady = status.data?.catalogReady;

	return (
		<>
			<PageHead
				title="Publicités"
				sub="Campagnes catalogue Advantage+ (pubs dynamiques) via Meta Marketing API."
				actions={
					<div style={{ display: "flex", gap: 8 }}>
						<Tag variant={enabled ? "success" : "neutral"}>
							{enabled ? "Publicités connectées" : "Non connectées"}
						</Tag>
						<Tag variant={catalogReady ? "success" : "warning"}>
							{catalogReady ? "Catalogue prêt" : "Catalogue manquant"}
						</Tag>
					</div>
				}
			/>

			{!enabled && (
				<div
					style={{
						display: "flex",
						gap: 10,
						alignItems: "flex-start",
						background: "var(--a-info-soft)",
						border: "1px solid var(--a-border)",
						borderRadius: "var(--a-r)",
						padding: "12px 14px",
						marginBottom: 18,
						fontSize: 12.5,
						color: "var(--a-ink-2)",
					}}
				>
					<span
						style={{
							color: "var(--a-info)",
							display: "inline-flex",
							marginTop: 1,
						}}
					>
						<AdminIcon n="info" s={15} />
					</span>
					<span>
						La connexion publicitaire (permission <code>ads_management</code>)
						s’active après validation de l’App Review Meta. La création reste
						disponible ensuite ; les campagnes sont créées
						<strong> en pause</strong> (aucune dépense sans activation
						manuelle).
					</span>
				</div>
			)}

			<Card style={{ marginBottom: 18 }}>
				<CardHead
					title="Nouvelle campagne catalogue"
					sub="Créée en pause — vous l’activez ensuite depuis la liste."
				/>
				<CardBody>
					<FormRow label="Nom de la campagne" required>
						<input
							className="inp"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Ex. Retargeting téléphones — juillet"
						/>
					</FormRow>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
							gap: 14,
						}}
					>
						<FormRow label={`Budget quotidien (${bMeta.currency})`}>
							<input
								type="number"
								className="inp"
								min={bMeta.min}
								step={bMeta.step}
								value={dailyBudget ?? ""}
								onChange={(e) => setDailyBudget(Number(e.target.value))}
							/>
						</FormRow>
						<FormRow label="Audience">
							<select
								className="inp"
								value={audience}
								onChange={(e) =>
									setAudience(e.target.value as "retargeting" | "prospecting")
								}
							>
								<option value="retargeting">
									Retargeting (visiteurs récents non acheteurs)
								</option>
								<option value="prospecting">
									Prospection (audience élargie)
								</option>
							</select>
						</FormRow>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: 4,
							paddingTop: 14,
							borderTop: "1px solid var(--a-border)",
						}}
					>
						<AdminButton
							variant="primary"
							disabled={
								name.trim().length < 3 || dailyBudget == null || create.isPending
							}
							onClick={() => create.mutate()}
						>
							{create.isPending ? "Création…" : "Créer la campagne (en pause)"}
						</AdminButton>
					</div>
				</CardBody>
			</Card>

			<Card>
				<CardHead
					title="Campagnes"
					sub={
						enabled
							? `${campaigns.data?.length ?? 0} campagne(s)`
							: "Connexion requise"
					}
					right={
						<AdminButton
							variant="ghost"
							size="sm"
							onClick={() => void campaigns.refetch()}
						>
							Rafraîchir
						</AdminButton>
					}
				/>
				<CardBody>
					{!enabled ? (
						<p
							style={{
								textAlign: "center",
								padding: "24px 0",
								fontSize: 12.5,
								color: "var(--a-ink-mute)",
							}}
						>
							Connectez les publicités (App Review) pour voir vos campagnes.
						</p>
					) : campaigns.isLoading ? (
						<p
							style={{
								textAlign: "center",
								padding: "24px 0",
								fontSize: 12.5,
								color: "var(--a-ink-mute)",
							}}
						>
							Chargement…
						</p>
					) : !campaigns.data?.length ? (
						<p
							style={{
								textAlign: "center",
								padding: "24px 0",
								fontSize: 12.5,
								color: "var(--a-ink-mute)",
							}}
						>
							Aucune campagne pour l’instant.
						</p>
					) : (
						<div>
							{campaigns.data.map((c, i) => {
								const st = statusTag(c.effectiveStatus, c.status);
								const isActive = (c.effectiveStatus ?? c.status) === "ACTIVE";
								const ins = c.insights ?? {};
								return (
									<div
										key={c.id}
										style={{
											padding: "14px 0",
											...(i > 0
												? { borderTop: "1px solid var(--a-border)" }
												: {}),
											display: "flex",
											flexDirection: "column",
											gap: 10,
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												gap: 10,
												flexWrap: "wrap",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
												}}
											>
												<span style={{ fontWeight: 600, fontSize: 13.5 }}>
													{c.name}
												</span>
												<Tag variant={st.cls}>{st.label}</Tag>
											</div>
											<AdminButton
												variant={isActive ? "secondary" : "primary"}
												size="sm"
												disabled={toggle.isPending}
												onClick={() => toggle.mutate(c)}
											>
												{isActive ? "Mettre en pause" : "Activer"}
											</AdminButton>
										</div>
										<div
											style={{
												display: "grid",
												gridTemplateColumns:
													"repeat(auto-fit, minmax(110px, 1fr))",
												gap: 10,
											}}
										>
											<Metric
												label="Budget/j"
												value={fmtMoney(c.dailyBudget, currency)}
											/>
											<Metric
												label="Dépense"
												value={fmtMoney(ins.spend, currency)}
											/>
											<Metric
												label="Portée"
												value={(ins.reach ?? 0).toLocaleString("fr-FR")}
											/>
											<Metric
												label="Achats"
												value={(ins.purchases ?? 0).toLocaleString("fr-FR")}
											/>
											<Metric
												label="ROAS"
												value={ins.roas ? `${ins.roas.toFixed(2)}×` : "—"}
											/>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</CardBody>
			</Card>
		</>
	);
}

function Metric({ label, value }: { label: string; value: string }) {
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<span
				style={{
					fontSize: 11,
					color: "var(--a-ink-mute)",
					letterSpacing: "0.03em",
				}}
			>
				{label}
			</span>
			<span className="tn" style={{ fontWeight: 600, fontSize: 13 }}>
				{value}
			</span>
		</div>
	);
}
